/**
 * BYO v2 — persistence payload builder.
 *
 * Builds the BYO source-creation payload for a Ray JOB, DELIBERATELY EXCLUDING
 * the pipeline vestiges that are meaningless for BYO:
 *   - `runtime.cluster_id` — Ray self-places; no cluster selection.
 *   - `poll_frequency` — no polling.
 *   - `output.dir.name.pattern` — replaced by output-path naming
 *     (`prepend_input_filename` / `prepend_input_dir`).
 *
 * It reuses byo/core (`ensureCodeContainerId`, `buildSourceConfig`) so the
 * source config + code-container construction does not drift from DirectFlow.
 * This does NOT touch the existing DirectFlow/SourceConfigureStep path — it is
 * the v2-only builder the new shells call.
 */

import {
  buildSourceConfig,
  ensureCodeContainerId,
  type CodeContainerPayload,
  type DispatchLike,
} from 'new/components/flows/types/byo/core/services';
import type { ByoCodeMode } from 'new/components/flows/types/byo/core/model';
import {
  BYO_BACKEND_FLOW_TYPE,
  withByoRuntimeMode,
} from 'new/components/flows/types/byo/core/model';
import type { ByoInputValue, ByoOutputValue } from './types';

/**
 * The set of vestige keys we strip from any source_config we are handed.
 *
 * REVERTED 2026-08-23: `packages`/`extra_data` were briefly added here
 * (2026-08-21), on the premise that BYO v2 never reads them back from
 * `source_config` (true — `code_config` on the code container is the real,
 * Ray-consumed contract) so their presence was "inert garbage" worth
 * cleaning up. That premise doesn't license removing them: `develop`'s own
 * current, live DirectFlow custom-processor path
 * (`SourceConfigureStep.tsx`'s `buildSourceInfo`) sends them through to
 * `source_config` on every save via the exact same
 * `createDefaultSourceConfig` this file also calls, and always has —
 * confirmed by reading develop's checked-in code directly, not inferred.
 * BYO v2 replicates that existing, established behavior rather than
 * diverging from it. Whether anything downstream actually reads
 * `source_config.packages` is a separate question from whether this is the
 * correct thing to replicate; it is what the rest of the app does.
 *
 * `custom_runtime.mode` REMOVED from this list 2026-09-01: traced
 * `probe-http`'s `ProbeApiHandler.scala:1356-1374` directly and confirmed it
 * reads this field to pick REPLICATION/PARSING/OFF for file-preview sampling
 * — a real backend consumer outside the IMC/Ray path this file's header
 * comment was reasoning about. Stripping it silently defaulted BYO's own
 * file preview to PARSING. See {@link BYO_CUSTOM_RUNTIME_MODE}, now written
 * explicitly by `buildByoSourcePayload` below instead of stripped.
 *
 * `start.cron` REMOVED from this list 2026-09-01: the BYO canvas step
 * deliberately keeps the connector's Scheduling section visible
 * (`byoConfigureStep.module.scss`'s `.wrap` only hides `.left-nav` and
 * `#section-heading-data-format`, NOT `#section-scheduling`/MonitoringInterval
 * — see that file's comment) so the user can pick a real cron schedule. That
 * value flows into `sourceConfig` the same as any other connector field and
 * was being silently discarded here on every save — the schedule picker was
 * live but non-functional. `ctrl-jobscheduler`'s `JobScheduler.scala`
 * `doStartByCron` (~line 994) reads `SourceConnectorConfig.START_CRON` for
 * any active source connector with no `flow_type` exclusion and no-ops (just
 * logs) when absent, so an unset value is still safe/on-demand-only — this
 * removal only stops throwing away a value the user actually chose. `'start'`
 * (the legacy nested `{cron: ...}` shape) stays stripped — no live BYO writer
 * of that shape was found.
 */
export const VESTIGE_SOURCE_CONFIG_KEYS = [
  'runtime.cluster_id',
  'cluster_id',
  'start',
  'poll_frequency',
  'output.dir.name.pattern',
] as const;

/** Remove the pipeline-vestige keys from a source_config object (non-mutating). */
export function stripVestigeFields<T extends Record<string, unknown>>(config: T): T {
  const next: Record<string, unknown> = { ...config };
  for (const key of VESTIGE_SOURCE_CONFIG_KEYS) {
    delete next[key];
  }
  return next as T;
}

/**
 * UI-only fields some hydration paths attach to a source's *local form
 * state* for display, never fields that belong in persisted source_config:
 * `initializeFormToFileCategory` (createFlowService.ts) decodes the attached
 * code container's code into `customProcessorCode` (+ `customProcessorId`)
 * purely so a code-editor UI can pre-fill it, and useStartSource's edit-flow
 * hydration adds `sourceName`/`sourceDescription`/`flow_triggers`. The
 * legacy DirectFlow wizard's own save path (SourceConfigureStep.tsx's
 * buildSourceInfo) has always stripped these before sending. Centralized
 * here — alongside the vestige keys, which every BYO v2 save path must also
 * strip — so both save paths (useByoSourceSave.ts, ByoSourceConfigureStep.tsx)
 * consult one list instead of maintaining their own; a source-edit save that
 * skipped this once already leaked raw decoded Python source into
 * source_config. Confirmed bug, fixed 2026-08-31.
 */
export const UI_ONLY_SOURCE_CONFIG_KEYS = [
  'customProcessorCode',
  'customProcessorId',
  'sourceName',
  'sourceDescription',
  'flow_triggers',
] as const;

/**
 * Strip both the UI-only convenience keys and the pipeline-vestige keys from
 * a source_config object (non-mutating). The one function every BYO v2 save
 * path should run its source_config through before persisting.
 */
export function sanitizeSourceConfigForSave<T extends Record<string, unknown>>(config: T): T {
  const next: Record<string, unknown> = { ...config };
  for (const key of UI_ONLY_SOURCE_CONFIG_KEYS) {
    delete next[key];
  }
  return stripVestigeFields(next as T);
}

export interface ByoCodeForPersistence {
  mode: ByoCodeMode;
  code: string;
  predefinedId?: number | string | null;
  /** Canonical internal package list. */
  packages: string[];
  extraData: string;
}

export interface BuildByoSourcePayloadArgs {
  input: ByoInputValue;
  code: ByoCodeForPersistence;
  /** Args for core `buildSourceConfig` (createFlowService default source config). */
  sourceConfigArgs: Parameters<typeof buildSourceConfig>[0];
  performanceConfig?: {
    cpu: number;
    memory: number;
    timeout: number;
    retries: number;
    concurrency: number;
  };
  name?: string;
  description?: string;
  /**
   * Existing code container id to UPDATE (PUT) instead of creating a new one.
   * Pass this when editing a source that already has an attached container —
   * omit it when creating a brand-new source.
   */
  existingContainerId?: number | string | null;
  /**
   * Whether the resulting container should be marked reusable (selectable
   * from "Use Existing Function" elsewhere). Omit to leave it unset.
   */
  reusable?: boolean;
  /**
   * The extra_data already saved on `existingContainerId`, so an in-place
   * update preserves the keys the UI doesn't model (see mergeExtraData in
   * core/services.ts). Only meaningful together with `existingContainerId`.
   */
  baseExtraData?: string;
}

export interface ByoSourcePayload {
  name?: string;
  description?: string;
  flow_type: typeof BYO_BACKEND_FLOW_TYPE;
  code_container_id: number | string | null;
  source_config: Record<string, unknown>;
}

/**
 * Build the clean BYO source-creation payload.
 *
 * - Resolves/creates the code container: `custom` code text creates one;
 *   `predefined` with a picked id creates a `source_custom` copy of it
 *   (see {@link ensureCodeContainerId}); `predefined` with no id and no code
 *   text yet (nothing chosen) resolves to `null` — no container created.
 * - Builds the source config via core `buildSourceConfig`, then STRIPS every
 *   vestige key.
 * - Sets `flow_type: 'custom'` and the selected file path.
 *
 * The output-path naming controls live on the SINK config, not here (see
 * {@link buildByoSinkOutputNaming}).
 */
export async function buildByoSourcePayload(
  args: BuildByoSourcePayloadArgs,
  deps: {
    dispatch: DispatchLike;
    createCodeContainerAction: (payload: CodeContainerPayload) => any;
    updateCodeContainerAction?: (id: number | string, payload: CodeContainerPayload) => any;
  },
): Promise<ByoSourcePayload> {
  const codeContainerId = await ensureCodeContainerId(
    {
      mode: args.code.mode,
      code: args.code.code,
      predefinedId: args.code.predefinedId,
      // serializePackages (comma-separated — see model.ts) is applied inside
      // buildCodeContainerPayload.
      packages: args.code.packages,
      extraData: args.code.extraData,
    },
    {
      dispatch: deps.dispatch,
      createCodeContainerAction: deps.createCodeContainerAction,
      updateCodeContainerAction: deps.updateCodeContainerAction,
      existingContainerId: args.existingContainerId,
      options: {
        name: args.name,
        description: args.description,
        performanceConfig: args.performanceConfig,
        reusable: args.reusable,
        baseExtraData: args.existingContainerId != null ? args.baseExtraData : undefined,
      },
    },
  );

  const rawSourceConfig = buildSourceConfig(args.sourceConfigArgs) as Record<string, unknown>;
  const source_config: Record<string, unknown> = withByoRuntimeMode(
    stripVestigeFields(rawSourceConfig),
  );

  // The selected file path drives Ray's listing/processing.
  if (args.input.folderPath) source_config.path = args.input.folderPath;
  if (args.input.fileType) source_config['file.type'] = args.input.fileType;

  return {
    ...(args.name !== undefined ? { name: args.name } : {}),
    ...(args.description !== undefined ? { description: args.description } : {}),
    flow_type: BYO_BACKEND_FLOW_TYPE,
    code_container_id: codeContainerId,
    source_config,
  };
}

/**
 * Build the BYO output-path naming fragment for the sink config.
 * Merge this into the sink config the shell builds via core `buildSinkConfig`.
 */
export function buildByoSinkOutputNaming(output: ByoOutputValue): Record<string, unknown> {
  return {
    prepend_input_filename: !!output.prependInputFilename,
    prepend_input_dir: !!output.prependInputDir,
    ...(output.target ? { path: output.target } : {}),
  };
}
